import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from './api';

const AuthContext = createContext(null);

// Ogni quanto ricontrollare i permessi in background (ms)
const INTERVALLO_AGGIORNAMENTO = 60000;

export function AuthProvider({ children }) {
  const [utente, setUtente] = useState(() => {
    const salvato = sessionStorage.getItem('utente');
    return salvato ? JSON.parse(salvato) : null;
  });

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Rilegge ruolo e permessi correnti dal backend (che li prende dal DB, non dal token)
  const aggiornaMe = useCallback(async () => {
    if (!sessionStorage.getItem('token')) return;
    try {
      const r = await API.get('/me', { silenzioso: true });
      const { ruolo, permessi, nome, cognome, ambiente } = r.data;
      setUtente(prev => {
        if (!prev) return prev;
        const stessi = prev.ruolo === ruolo && JSON.stringify(prev.permessi || []) === JSON.stringify(permessi || []);
        if (stessi && prev.nome === nome && prev.cognome === cognome && prev.ambiente === ambiente) return prev;
        const aggiornato = { ...prev, ruolo, permessi: permessi || [], nome, cognome, ambiente };
        sessionStorage.setItem('utente', JSON.stringify(aggiornato));
        return aggiornato;
      });
    } catch (e) {
      // 401 (utente disattivato o token scaduto) è gestito dall'interceptor di api.js
    }
  }, []);

  useEffect(() => {
    if (!utente) return undefined;
    aggiornaMe();
    const t = setInterval(aggiornaMe, INTERVALLO_AGGIORNAMENTO);
    const suFocus = () => { if (document.visibilityState === 'visible') aggiornaMe(); };
    window.addEventListener('focus', suFocus);
    document.addEventListener('visibilitychange', suFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', suFocus);
      document.removeEventListener('visibilitychange', suFocus);
    };
  }, [utente?.id, aggiornaMe]);  // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username, password) => {
    const r = await API.post('/login', { username, password });
    const { access_token, utente: u } = r.data;
    sessionStorage.setItem('token', access_token);
    sessionStorage.setItem('utente', JSON.stringify(u));
    API.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUtente(u);
    return u;
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('utente');
    delete API.defaults.headers.common['Authorization'];
    setUtente(null);
  };

  const haPermesso = (codice) => {
    return utente?.permessi?.includes(codice) || false;
  };

  return (
    <AuthContext.Provider value={{ utente, login, logout, haPermesso, aggiornaMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
